import {
  LegacyRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { Text } from '@raystack/apsara';

import { Show } from './..';

import styles from './VirtualizedFor.module.css';

interface VirtualizedFor<T> {
  children: (props: ListChildComponentProps) => React.ReactNode;
  itemHeight: number;
  data: T;
  totalCount?: number;
  fetchMoreItems?: () => void;
  isMoreLoading?: boolean;
}

export const VirtualizedFor = forwardRef(
  <T,>(
    {
      children,
      itemHeight,
      data,
      totalCount,
      fetchMoreItems,
      isMoreLoading = false
    }: VirtualizedFor<T>,
    ref: LegacyRef<FixedSizeList<T>> | undefined
  ) => {
    const [meta, setMeta] = useState({ width: 0, height: 0 });

    const divRef = useRef<HTMLDivElement>(null);
    const outerRef = useRef<HTMLDivElement>(null);

    const count = useMemo(() => (data as typeof Array).length, [data]);

    useEffect(() => {
      if (!divRef.current) return;

      const parentElement = divRef.current.parentElement;

      if (!parentElement) return;

      const rect = parentElement.getBoundingClientRect();
      const style = window.getComputedStyle(parentElement);

      const paddingTop = Number(style.paddingTop.replace('px', ''));
      const paddingBottom = Number(style.paddingBottom.replace('px', ''));
      const paddingRight = Number(style.paddingRight.replace('px', ''));
      const paddingLeft = Number(style.paddingLeft.replace('px', ''));

      const height = Number(style.height.replace('px', ''));

      setMeta({
        width: rect.width - paddingRight - paddingLeft,
        height: height - (paddingTop + paddingBottom)
      });
    }, []);

    const onScroll = () => {
      if (!fetchMoreItems || !count || isMoreLoading || count === totalCount) {
        return;
      }

      const lastElement = (outerRef.current as HTMLDivElement).lastChild
        ?.lastChild as HTMLDivElement;

      if (Number(lastElement.getAttribute('data-index')) !== count - 1) return;

      const parentRect =
        outerRef.current?.parentElement?.getBoundingClientRect();

      if (!parentRect) return;

      const childRect = lastElement.getBoundingClientRect();

      if (childRect.top <= parentRect.bottom) fetchMoreItems();
    };

    return (
      <Show
        when={!!(meta.width && meta.height)}
        fallback={<div ref={divRef} />}
      >
        <FixedSizeList
          itemCount={count}
          itemSize={itemHeight}
          height={meta.height - (isMoreLoading ? 32 : 0)}
          width={meta.width}
          itemData={data}
          onScroll={onScroll}
          outerRef={outerRef}
          ref={ref}
          className={styles.fixedSizeList}
        >
          {children}
        </FixedSizeList>

        <Show when={isMoreLoading}>
          <Text
            className={styles.loader}
            size={5}
            variant='accent'
            weight={500}
          >
            Loading...
          </Text>
        </Show>
      </Show>
    );
  }
);

VirtualizedFor.displayName = 'VirtualizedFor';
